/** Alliance constants, roles and the permission matrix (82-alliance.md, D71). The server checks permissions in one place. */

export const ALLIANCE_CREATE_HQ = 3;
export const ALLIANCE_CREATE_CREDITS = 100;
export const ALLIANCE_BASE_CAP = 30;
export const ALLIANCE_MAX_CAP = 50;
export const ALLIANCE_JOIN_COOLDOWN_MS = 3_600_000;
export const ALLIANCE_OFFICER_MAX = 5;
/** Members' reads of another base are rate-limited per minute. */
export const ALLIANCE_VISITS_PER_MINUTE = 30;

export const ALLIANCE_ROLES = ["leader", "officer", "veteran", "member"] as const;
export type AllianceRole = (typeof ALLIANCE_ROLES)[number];

export type AlliancePermission = "invite" | "accept" | "kick" | "promote" | "notice" | "settings" | "diplomacy" | "focus" | "disband" | "transfer";

export const ALLIANCE_PERMISSIONS: Record<AllianceRole, readonly AlliancePermission[]> = {
  leader: ["invite", "accept", "kick", "promote", "notice", "settings", "diplomacy", "focus", "disband", "transfer"],
  officer: ["invite", "accept", "kick", "promote", "notice", "settings", "diplomacy", "focus"],
  veteran: ["invite"],
  member: [],
};

export function allianceCan(role: AllianceRole, permission: AlliancePermission): boolean {
  return ALLIANCE_PERMISSIONS[role].includes(permission);
}

/** Rank order for "only roles below your own": leader 0 … member 3. */
export function allianceRank(role: AllianceRole): number {
  return ALLIANCE_ROLES.indexOf(role);
}

export const ALLIANCE_NAME = /^[\p{L}\p{N} _'-]{3,24}$/u;
export const ALLIANCE_TAG = /^[A-Z0-9]{2,4}$/;
export const ALLIANCE_JOIN_POLICIES = ["open", "approval", "closed"] as const;
export type AllianceJoinPolicy = (typeof ALLIANCE_JOIN_POLICIES)[number];
export const ALLIANCE_BANNERS = ["steel", "ember", "verdant", "cobalt", "amber", "crimson"] as const;
export type AllianceBanner = (typeof ALLIANCE_BANNERS)[number];

// ─── diplomacy (82-alliance.md R79, D73) ───

/** Relations between two alliances, their timers and the modifiers they carry into raids and assists. */
export const DIPLOMACY = {
  relations: ["neutral", "allied", "hostile", "war"] as const,
  /** A proposal (alliance or peace) waits this long for an answer. */
  proposalTtlMs: 48 * 3_600_000,
  /** Breaking an alliance takes this notice; both sides see the countdown. */
  breakNoticeMs: 24 * 3_600_000,
  /** Hostile can go back to neutral after this long. */
  hostileToNeutralMs: 24 * 3_600_000,
  /** War needs this long of hostility … */
  warGateHostileMs: 24 * 3_600_000,
  /** … or this many raids by the target on the declarer's members in the last 24 h. */
  warGateRaids: 3,
  warGateRaidWindowMs: 24 * 3_600_000,
  /** A declaration starts this countdown before the war is on. */
  warCountdownMs: 12 * 3_600_000,
  warDurationMs: 72 * 3_600_000,
  /** Extra loot on raids by relation. Allied raids are refused. */
  lootPct: { neutral: 0, allied: 0, hostile: 10, war: 25 } as Record<"neutral" | "allied" | "hostile" | "war", number>,
  warScorePerWin: 1,
  /** Assists between allied members (same alliance or allied) count this many times for events. */
  alliedAssistMultiplier: 2,
  /** The side with the higher war score gets this gift for every member (D72 gifts). */
  warGiftTier: "gold" as const,
} as const;
export type Relation = (typeof DIPLOMACY.relations)[number];
export const PROPOSAL_KINDS = ["alliance", "peace"] as const;
export type ProposalKind = (typeof PROPOSAL_KINDS)[number];

// ─── alliance duel and bot alliances (82-alliance.md R80, D74) ───

/** The weekly duel: themed sources count double, milestones drop gifts, the winners get a chest and the alliance XP. */
export const ALLIANCE_DUEL = {
  themes: ["build", "units", "kills", "gather", "all"] as const,
  themeSources: {
    build: ["build.level", "research.level"],
    units: ["unit.level", "workshop.pull"],
    kills: ["pack.kill", "elite.kill", "hunt.kill"],
    gather: ["gather"],
    all: [],
  } as Record<string, readonly string[]>,
  themeMultiplier: 2,
  /** Alliance points to beat when no opponent is available, and the base of the milestones. */
  target: 4000,
  milestonePct: [25, 50, 100] as const,
  milestoneTiers: ["bronze", "silver", "gold"] as const,
  winnerChest: "itm.chest_gold",
  winnerXp: 300,
} as const;
export type DuelTheme = (typeof ALLIANCE_DUEL.themes)[number];

/** Roster bots found these alliances (round-robin by roster index) so duels and diplomacy have counterparts on a small server. */
export const BOT_ALLIANCES: readonly { name: string; tag: string; banner: AllianceBanner }[] = [
  { name: "Ash Wardens", tag: "ASHW", banner: "ember" },
  { name: "Iron Furrow", tag: "IRON", banner: "steel" },
  { name: "Cobalt Reach", tag: "CBLT", banner: "cobalt" },
  { name: "Verdant Line", tag: "VERD", banner: "verdant" },
];
export const BOT_ALLIANCE_LINES: readonly string[] = [
  "Depot ist voll, wer braucht Rationen?",
  "Hab heute gespendet. Musterhalle zuerst?",
  "Wer hilft beim Ausbau? Bauhilfe ist offen.",
  "Karte im Norden ist ruhig. Sammler raus.",
  "Duell läuft, jeder Abschuss zählt.",
  "Donated today. Focus the Trade Hub next?",
  "Night shift on the wall. All quiet.",
];
